import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzFilterOptionType, NzSelectModule } from 'ng-zorro-antd/select';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { VariablesService } from '../../../services/variables/variables.service';
import { IColumnDefinition } from '../../standard-tile/standard-tile.model';
import { ResolvedTrackingLaneNode, TrackingLaneNode, TrackingLaneNodeStatus } from '../../../model/iTrackingLane';
import { resolveTrackingLaneNodes } from '../tracking-lane.resolver';

@Component({
  selector: 'tracking-lane-config',
  standalone: true,
  imports: [ CommonModule, FormsModule, CdkDropList, CdkDrag, CdkDragHandle, NzInputModule, NzSelectModule, NzButtonModule,
    NzIconModule, NzToolTipModule ],
  templateUrl: './tracking-lane-config.component.html',
  styleUrl: './tracking-lane-config.component.scss'
})
export class TrackingLaneConfigComponent implements OnInit {
  private variablesService = inject(VariablesService);

  @Input() nodes: TrackingLaneNode[] = [];
  @Output() nodesChange = new EventEmitter<TrackingLaneNode[]>();

  @Input() currentNodeId: string | null = null;
  @Output() currentNodeIdChange = new EventEmitter<string | null>();

  columnDefinitions: IColumnDefinition[] = [];

  ngOnInit(): void {
    this.variablesService.getColumnDefinitions().subscribe((columns: IColumnDefinition[]) => {
      this.columnDefinitions = (columns ?? []).slice().sort((a, b) =>
        (a.headerName ?? a.field ?? '').localeCompare(b.headerName ?? b.field ?? '')
      );
    });
  }

  addRootNode(): void {
    this.nodes.push(this.createNode());
    this.emitNodes();
  }

  addChildNode(parent: TrackingLaneNode): void {
    parent.children.push(this.createNode());
    this.emitNodes();
  }

  removeNode(siblings: TrackingLaneNode[], node: TrackingLaneNode): void {
    const index = siblings.indexOf(node);
    if (index === -1) return;
    siblings.splice(index, 1);
    if (this.currentNodeId === node.id) {
      this.currentNodeIdChange.emit(null);
    }
    this.emitNodes();
  }

  drop(event: CdkDragDrop<TrackingLaneNode[]>, siblings: TrackingLaneNode[]): void {
    moveItemInArray(siblings, event.previousIndex, event.currentIndex);
    this.emitNodes();
  }

  selectCurrent(node: TrackingLaneNode): void {
    this.currentNodeIdChange.emit(this.currentNodeId === node.id ? null : node.id);
  }

  filterVariableOption: NzFilterOptionType = (input, option) => {
    const term = input.toLowerCase();
    const label = (option.nzLabel ?? '').toString().toLowerCase();
    const value = (option.nzValue ?? '').toString().toLowerCase();
    return label.includes(term) || value.includes(term);
  };

  statusFor(node: TrackingLaneNode): TrackingLaneNodeStatus {
    const map = new Map<string, TrackingLaneNodeStatus>();
    const collect = (list: ResolvedTrackingLaneNode[]): void => {
      for (const resolved of list) {
        map.set(resolved.id, resolved.status);
        collect(resolved.children);
      }
    };
    collect(resolveTrackingLaneNodes(this.nodes, this.currentNodeId));
    return map.get(node.id) ?? 'upcoming';
  }

  emitNodes(): void {
    this.nodesChange.emit(this.nodes);
  }

  private createNode(): TrackingLaneNode {
    return {
      id: crypto.randomUUID(),
      data: { name: '', variable_as_is: '' },
      children: []
    };
  }
}
